import type { LocationQuery } from 'vue-router'
import type { SUPPORTED_QUERY_PARAMETERS } from '~/constants/query-parameters'

const TRUTHY_FLAG_VALUES = new Set(['', '1', 'true'])

/**
 * Gets a query parameter from the query string.
 *
 * @param query The query string.
 * @param parameter The parameter to get.
 * @returns The parameter value.
 */
export function getQueryParameter(query: LocationQuery, parameter: SUPPORTED_QUERY_PARAMETERS) {
  return Array.isArray(query[parameter]) ? query[parameter][0] : query[parameter]
}

/**
 * Gets a boolean query parameter. We serialize flags without a value (`?Delivery`),
 * but hand-written, marketing and legacy URLs also use `?Delivery=true`, so both
 * are accepted.
 *
 * @param query The query string.
 * @param parameter The parameter to get.
 * @returns Whether the flag is enabled.
 */
export function getQueryParameterFlag(query: LocationQuery, parameter: SUPPORTED_QUERY_PARAMETERS) {
  const value = getQueryParameter(query, parameter)

  if (value === undefined) {
    return false
  }

  return value === null || TRUTHY_FLAG_VALUES.has(value.trim().toLowerCase())
}

/**
 * Gets a numeric query parameter. A parameter without a value (`?MinPrice`) or with
 * an empty one (`?MinPrice=`) is absent rather than zero, which is what `Number()`
 * would otherwise make of it.
 *
 * @param query The query string.
 * @param parameter The parameter to get.
 * @returns The parameter value, or undefined when it cannot be read as a number.
 */
export function getQueryParameterNumber(query: LocationQuery, parameter: SUPPORTED_QUERY_PARAMETERS) {
  const value = getQueryParameter(query, parameter)

  if (value === undefined || value === null || value.trim() === '') {
    return undefined
  }

  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Gets a comma-separated query parameter as a list of exact values, so that
 * matching against it cannot succeed on a partial value.
 *
 * @param query The query string.
 * @param parameter The parameter to get.
 * @returns The parameter values.
 */
export function getQueryParameterList(query: LocationQuery, parameter: SUPPORTED_QUERY_PARAMETERS) {
  const value = getQueryParameter(query, parameter)

  if (!value) {
    return []
  }

  return value.split(',').map((entry) => entry.trim()).filter(Boolean)
}
